import { draggable } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { ArrowDownward, ArrowUpward, Flag, Timer } from "@mui/icons-material";
import { Box, Chip, Typography } from "@mui/joy";
import Card from "@mui/joy/Card";
import Stack from "@mui/joy/Stack";
import { useNavigate } from "@tanstack/react-router";
import { ApiTask } from "griptonite-api-client/types/tasks";
import { useEffect, useRef, useState } from "react";

import MiscTask from "@/assets/icons/MiscTask";
import Warning from "@/assets/icons/Warning";

import { RouteName } from "../RoutesTable/RouteName";
import { getTaskDragData } from "../TaskSetOverview/dragData";
import { useTaskSetOverviewStore } from "../TaskSetOverview/useTaskSetOverview";
import Assignees from "./Assignees";
import StatusChip from "./StatusChip";
import TraitIcons from "./TraitIcons";

type Props = {
  task: ApiTask;
  displayMode?: "status" | "grade";
  isActive?: boolean;
};

const truncate = (text: string | null) => {
  if (!text) return "";
  if (text.length > 60) {
    return text.slice(0, 60) + "...";
  }
  return text;
};

export default function TaskCard({ task, displayMode = "status", isActive }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [, setDragging] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    return draggable({
      element: ref.current,
      onDragStart: () => setDragging(true),
      onDrop: () => setDragging(false),
      getInitialData: () => getTaskDragData(task),
    });
  }, [task]);

  const { setContextMenu } = useTaskSetOverviewStore();
  const navigate = useNavigate();

  const icon = {
    SET: <ArrowUpward sx={{ color: "success.500" }} />,
    STRIP: <ArrowDownward sx={{ color: "danger.500" }} />,
    MISC: <MiscTask size="sm" sx={{ color: "#00C8D5" }} />,
  }[task.type];
  return (
    <Card
      ref={ref}
      size="sm"
      sx={{
        width: "288px",
        height: "108px",
        minHeight: "100px",
        cursor: "pointer",
        boxShadow: "md",
        flexShrink: 0,
        textDecoration: "none",
        zIndex: 1000,
        // touchAction: "none",
        ...(isActive
          ? {
              border: "2px solid",
              borderColor: "primary.400",
            }
          : {}),
        ...(task.flagged
          ? {
              border: "2px solid",
              borderColor: "warning.400",
            }
          : {}),
        position: "relative",
      }}
      onContextMenuCapture={(e) => {
        e.preventDefault();
        // Pointer devices only: on touch this fires from a long press, which
        // conflicts with dragging a card between columns.
        if (window.matchMedia("(hover: hover)").matches) {
          setContextMenu({
            task,
            x: e.clientX,
            y: e.clientY,
          });
        }
      }}
      onClick={() => {
        navigate({
          to: "/gym/$venueUuid/schedule/set/$setUuid/$taskUuid",
          replace: true,
          params: {
            venueUuid: task.venue,
            setUuid: task.set,
            taskUuid: task.uuid,
          },
        });
      }}
    >
      <Stack direction="row" spacing={0.5} alignItems={"center"}>
        {icon}
        <Typography
          level="title-md"
          fontWeight="bold"
          sx={{
            flex: 1,
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden",
          }}
        >
          {task.route ? <RouteName route={task.route} /> : task.name}
        </Typography>
        {task.type === "STRIP" && !task.route && <Warning />}
        {displayMode === "status" ? (
          <StatusChip status={task.status} />
        ) : (
          <Typography level="title-md" fontWeight="bold">
            {task.route?.gradeValue || task.gradeValue}
          </Typography>
        )}
        {task.flagged && <Flag sx={{ color: "warning.400" }} />}
      </Stack>
      <Stack sx={{ flex: 1 }}>
        {task.route ? (
          <TraitIcons route={task.route} />
        ) : (
          <Typography
            fontSize="title-sm"
            textColor="neutral.600"
            lineHeight={1.1}
            sx={{ marginTop: -0.5 }}
          >
            {truncate(task.notes)}
          </Typography>
        )}
      </Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          {task.route ? (
            <Typography level="body-sm" textColor="neutral.600" fontWeight="bold">
              {task.route?.circuit?.name || task.route?.startLine?.name || ""}
            </Typography>
          ) : null}
          {task.allocatedDuration && (
            <Chip size="sm" color="neutral" variant="outlined" startDecorator={<Timer />}>
              {task.allocatedDuration} min
            </Chip>
          )}
        </Box>
        <Assignees task={task} />
      </Stack>
    </Card>
  );
}
